/**
 * Os ábacos do CPTu na tela (onda 313, R5 e R6; spec 07 §5.2, item 3) — contas puras, sem DOM.
 *
 * Os ábacos vêm do motor, e só eles têm linha: a tela desenha as linhas que CLASSIFICAM, com os mesmos
 * números das funções de zona (`classificacao.ts`), e nenhuma outra.
 *  · SBTn (Qtn × Fr) e SBT (qt/pa × Rf): as oito polilinhas de `FRONTEIRAS_ROBERTSON`, vértice a vértice
 *    (em log × log; `f23` e `divisa89` dão x em função de y). Os círculos do Ic NÃO se desenham — não são o
 *    que classifica, e pelos círculos as zonas erram 6 a 7 % do gabarito.
 *  · SBTn modificado (Robertson 2016): as curvas I(B) = 22, I(B) = 32 e CD = 70 (`CURVAS_SBTN_MOD`, só
 *    fora da caixa CCS, onde elas separam zonas) e os dois lados da caixa CCS (`LIMITES_SBTN_MOD`).
 *  · Schneider et al. (2008), Q × U₂: as curvas de `CURVAS_SCHNEIDER` (o traçado do CPeT-IT, com
 *    `FONTE_SCHNEIDER` escrito e sem gabarito), cada uma no trecho em que separa zonas — inclusive a reta
 *    Bq = 1 (U₂ = Q) que o `zonaSchneider` usa para a 1c — e o funil da zona 2.
 *  · Qt1 × Bq: o motor não tem o ábaco de Robertson (1990) em Qt × Bq nem desenho digitalizado (o «Bq
 *    plots» do CPeT-IT é o Schneider): pontos sem linha nenhuma, e a frase que diz isso.
 *
 * Os eixos são os do CPeT-IT e da prancheta: SBTn e 2016 Fr 0,1–10 % × Qtn 1–1.000; SBT Rf 0,1–10 % ×
 * qt/pa 1–1.000 (log × log, a moldura de `MOLDURA_DO_ABACO`); Schneider U₂ LINEAR de −2 a 10 (o painel
 * linear do traçado, com os negativos dentro) × Q log de 1 a 1.000; Qt1 × Bq com o Bq linear pelo domínio
 * dos dados, o zero sempre dentro, × Qt1 log de 1 a 1.000. O ponto fora da moldura vai à borda, e a
 * contagem diz quantos.
 *
 * A cor do ponto: no SBTn, no SBT e no 2016, a zona do próprio ábaco (o `token` do motor); no Schneider e
 * no Qt1 × Bq, a zona SBTn da leitura (as zonas de Schneider não têm token, e não se cria token novo —
 * as regiões de Schneider levam só o rótulo escrito).
 *
 * O número de cada zona vai escrito num ponto que a própria função de zona do motor classifica como
 * aquela zona: o ponto da zona mais longe das outras e da moldura, numa grade de 60 × 60 no espaço dos
 * eixos.
 */
import {
  CURVAS_SBTN_MOD, CURVAS_SCHNEIDER, FONTE_SCHNEIDER, FRONTEIRAS_ROBERTSON, LIMITES_SBTN_MOD, MOLDURA_DO_ABACO,
  PA_KPA, ZONAS_SBTN_MOD, ZONAS_SCHNEIDER, zonaSbt, zonaSbtn, zonaSbtnMod, zonaSchneider,
} from '../motor';
import type { ColunaDoMotor, Polilinha, Premissas, Resultado } from '../motor';
import type { EscalaDoEixo } from './escalas';
import { numeroBr } from './escalas';
import { combinarMarcas, marcaDaColuna } from './homologacao';
import type { MarcaDeHomologacao } from './homologacao';

/** Os ábacos da tela. */
export type TipoDeAbaco = 'sbtn' | 'sbt' | 'sbtnMod' | 'schneider' | 'qtBq';

/** Um ponto do ábaco, nas unidades dos eixos. */
export interface PontoDoAbaco {
  x: number;
  y: number;
}

/** O desenho de um ábaco: os eixos, as linhas que classificam, os rótulos das zonas e a nota da fonte. */
export interface DesenhoDoAbaco {
  tipo: TipoDeAbaco;
  titulo: string;
  eixoX: { rotulo: string; escala: EscalaDoEixo; dominio: [number, number] | 'dados' };
  eixoY: { rotulo: string; escala: EscalaDoEixo; dominio: [number, number] };
  linhas: { chave: string; pontos: PontoDoAbaco[] }[];
  rotulos: { zona: number; texto: string; x: number; y: number }[];
  /** `FONTE_SCHNEIDER` no Schneider; no Qt1 × Bq, a frase de que não há fronteiras; null nos de Robertson. */
  fonte: string | null;
  semFronteiras: boolean;
}

/** A frase do Qt1 × Bq (R6): sem fronteiras, e a cor é a zona SBTn. */
export const FRASE_SEM_FRONTEIRAS_QT_BQ =
  'sem as fronteiras do ábaco Qt × Bq: o motor não tem a fonte digitalizada — a cor é a zona SBTn (Qtn × Fr)';

const { xMinimo, xMaximo, base, topo } = MOLDURA_DO_ABACO;
const { ccsFr, ccsQtn } = LIMITES_SBTN_MOD;

/** O U₂ do painel linear do Schneider (o traçado do CPeT-IT). */
const U2_MINIMO = -2;
const U2_MAXIMO = 10;

/** I(B) = 100·(Qtn + 10)/(70 + Qtn·Fr), a definição de Robertson (2016) que o básico usa (`basico.ts`). */
const ibDe = (fr: number, qtn: number): number => (100 * (qtn + 10)) / (70 + qtn * fr);

/** Uma polilinha de Robertson em pontos: y em função de x, ou x em função de y (`f23` e `divisa89`). */
function pontosDaPolilinha(f: Polilinha, xEmFuncaoDeY: boolean): PontoDoAbaco[] {
  return f.t.map((t, k) => (xEmFuncaoDeY ? { x: 10 ** f.v[k], y: 10 ** t } : { x: 10 ** t, y: 10 ** f.v[k] }));
}

/**
 * Uma curva amostrada em `passos` passos iguais do log da variável independente (de `de` a `ate`, as pontas
 * exatas), só onde ela existe e cai dentro da moldura (`dentro`), com a ponta de cada lado achada por
 * bissecção (do lado de dentro). A curva é suposta contínua: um trecho só.
 */
function curvaNaMoldura(
  ponto: (v: number) => PontoDoAbaco,
  de: number,
  ate: number,
  dentro: (p: PontoDoAbaco) => boolean,
  passos = 120,
): PontoDoAbaco[] {
  const l0 = Math.log10(de);
  const l1 = Math.log10(ate);
  const valorDoPasso = (k: number): number => (k === 0 ? de : k === passos ? ate : 10 ** (l0 + ((l1 - l0) * k) / passos));
  const vale = (p: PontoDoAbaco): boolean => Number.isFinite(p.x) && Number.isFinite(p.y) && dentro(p);
  const ponta = (vDentro: number, vFora: number): PontoDoAbaco => {
    let a = vDentro;
    let b = vFora;
    for (let k = 0; k < 60; k++) {
      const m = Math.sqrt(a * b); // a média no log
      if (vale(ponto(m))) a = m;
      else b = m;
    }
    return ponto(a);
  };
  const saida: PontoDoAbaco[] = [];
  let anterior: { v: number; p: PontoDoAbaco; ok: boolean } | null = null;
  for (let k = 0; k <= passos; k++) {
    const v = valorDoPasso(k);
    const p = ponto(v);
    const ok = vale(p);
    if (anterior && anterior.ok !== ok) {
      const q = ok ? ponta(v, anterior.v) : ponta(anterior.v, v);
      if (!ok) saida.push(q);
      else if (q.x !== p.x || q.y !== p.y) saida.push(q);
    }
    if (ok) saida.push(p);
    anterior = { v, p, ok };
  }
  return saida;
}

/** Dentro da moldura de Robertson (Fr ou Rf de 0,1 a 10; Qtn ou qt/pa de 1 a 1.000). */
const naMolduraDeRobertson = (p: PontoDoAbaco): boolean => p.x >= xMinimo && p.x <= xMaximo && p.y >= base && p.y <= topo;

/** Dentro da moldura do Schneider (U₂ de −2 a 10; Q de 1 a 1.000). */
const naMolduraDeSchneider = (p: PontoDoAbaco): boolean =>
  p.x >= U2_MINIMO && p.x <= U2_MAXIMO && p.y >= base && p.y <= topo;

/** Fora da caixa CCS do 2016 (Fr < 2 e Qtn < 10), onde as curvas I(B) e CD separam zonas. */
const foraDaCaixaCcs = (p: PontoDoAbaco): boolean => !(p.x < ccsFr && p.y < ccsQtn);

/** As linhas do SBTn modificado: I(B) = 22, I(B) = 32, CD = 70 (fora da caixa CCS) e a caixa CCS. */
function linhasDoSbtnMod(): DesenhoDoAbaco['linhas'] {
  const dentro = (p: PontoDoAbaco): boolean => naMolduraDeRobertson(p) && foraDaCaixaCcs(p);
  const curvaDoMod = (f: (fr: number) => number, de: number) => curvaNaMoldura((fr) => ({ x: fr, y: f(fr) }), de, xMaximo, dentro);
  return [
    // I(B) = 22 só sai da caixa CCS em Fr = 2 (Qtn 9,64): antes, a caixa decide.
    { chave: 'ib22', pontos: curvaDoMod(CURVAS_SBTN_MOD.ib22, ccsFr) },
    { chave: 'ib32', pontos: curvaDoMod(CURVAS_SBTN_MOD.ib32, xMinimo) },
    { chave: 'cd70', pontos: curvaDoMod(CURVAS_SBTN_MOD.cd70, xMinimo) },
    { chave: 'ccs', pontos: [{ x: ccsFr, y: base }, { x: ccsFr, y: ccsQtn }, { x: xMinimo, y: ccsQtn }] },
  ];
}

/**
 * As linhas do Schneider, cada uma no trecho em que separa zonas no `zonaSchneider` (x = U₂, y = Q):
 *  · `3|1a`, inteira (abaixo, zona 3; acima, 1a ou 1c);
 *  · `1a|1b` e `1b|1c`, só à esquerda da reta Bq = 1 (do lado de lá é tudo 1c: o desenho as termina nela);
 *  · `bq1`, U₂ = Q, só onde separa a 1a e a 1b da 1c: acima da `3|1a` e abaixo da `1b|1c`;
 *  · `zona2`, o funil |U₂| < 1 e Q > 20·e^(2·|U₂|), com as paredes em U₂ = ±1.
 */
function linhasDoSchneider(): DesenhoDoAbaco['linhas'] {
  const C = CURVAS_SCHNEIDER;
  const emQ = (f: (q: number) => number, dentro: (p: PontoDoAbaco) => boolean) =>
    curvaNaMoldura((q) => ({ x: f(q), y: q }), base, topo, (p) => naMolduraDeSchneider(p) && dentro(p), 240);
  const aEsquerdaDaBq1 = (p: PontoDoAbaco): boolean => p.x < C.bq1(p.y);
  const bq1 = emQ(C.bq1, (p) => p.x > C['3|1a'](p.y) && p.x <= C['1b|1c'](p.y));
  // O funil da zona 2: a curva Q = 20·e^(2·|U₂|) entre as paredes, que sobem até o topo.
  const parede = 1 - 1e-9;
  const funil: PontoDoAbaco[] = [{ x: -parede, y: topo }];
  for (let k = 0; k <= 80; k++) {
    const u2 = -parede + (2 * parede * k) / 80;
    funil.push({ x: u2, y: C.zona2QMinimo(u2) });
  }
  funil.push({ x: parede, y: topo });
  return [
    { chave: '3|1a', pontos: emQ(C['3|1a'], () => true) },
    { chave: '1a|1b', pontos: emQ(C['1a|1b'], aEsquerdaDaBq1) },
    { chave: '1b|1c', pontos: emQ(C['1b|1c'], aEsquerdaDaBq1) },
    { chave: 'bq1', pontos: bq1 },
    { chave: 'zona2', pontos: funil },
  ];
}

/** O lado da grade dos rótulos. */
const GRADE = 60;

/**
 * Os rótulos das zonas: para cada zona, o centro da célula da grade (60 × 60, no espaço dos eixos) mais
 * longe das outras zonas e da moldura (distância de chanfro 3-4), classificada pela própria função de
 * zona — por construção, o rótulo fica num ponto da zona dele.
 */
function rotulosDasZonas(
  zonaDe: (x: number, y: number) => number,
  eixoX: { escala: EscalaDoEixo; dominio: [number, number] },
  eixoY: { escala: EscalaDoEixo; dominio: [number, number] },
  zonas: readonly number[],
  texto: (zona: number) => string,
): DesenhoDoAbaco['rotulos'] {
  const valorNoEixo = (e: { escala: EscalaDoEixo; dominio: [number, number] }, u: number): number => {
    const [d0, d1] = e.dominio;
    return e.escala === 'log' ? 10 ** (Math.log10(d0) + u * (Math.log10(d1) - Math.log10(d0))) : d0 + u * (d1 - d0);
  };
  const xs = Array.from({ length: GRADE }, (_, i) => valorNoEixo(eixoX, (i + 0.5) / GRADE));
  const ys = Array.from({ length: GRADE }, (_, j) => valorNoEixo(eixoY, (j + 0.5) / GRADE));
  const grade = new Int8Array(GRADE * GRADE);
  for (let j = 0; j < GRADE; j++) for (let i = 0; i < GRADE; i++) grade[j * GRADE + i] = zonaDe(xs[i], ys[j]);

  const saida: DesenhoDoAbaco['rotulos'] = [];
  const d = new Float64Array(GRADE * GRADE);
  const em = (i: number, j: number): number => (i < 0 || j < 0 || i >= GRADE || j >= GRADE ? 0 : d[j * GRADE + i]);
  for (const zona of zonas) {
    for (let k = 0; k < d.length; k++) d[k] = grade[k] === zona ? Infinity : 0;
    for (let j = 0; j < GRADE; j++) {
      for (let i = 0; i < GRADE; i++) {
        const k = j * GRADE + i;
        if (d[k] === 0) continue;
        d[k] = Math.min(d[k], em(i - 1, j) + 3, em(i, j - 1) + 3, em(i - 1, j - 1) + 4, em(i + 1, j - 1) + 4);
      }
    }
    let melhor = -1;
    for (let j = GRADE - 1; j >= 0; j--) {
      for (let i = GRADE - 1; i >= 0; i--) {
        const k = j * GRADE + i;
        if (d[k] === 0) continue;
        d[k] = Math.min(d[k], em(i + 1, j) + 3, em(i, j + 1) + 3, em(i + 1, j + 1) + 4, em(i - 1, j + 1) + 4);
      }
    }
    for (let k = 0; k < d.length; k++) if (d[k] > 0 && (melhor < 0 || d[k] > d[melhor])) melhor = k;
    if (melhor < 0) continue;
    saida.push({ zona, texto: texto(zona), x: xs[melhor % GRADE], y: ys[Math.floor(melhor / GRADE)] });
  }
  return saida;
}

const NOVE = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
const SETE = [1, 2, 3, 4, 5, 6, 7] as const;
const CINCO = [1, 2, 3, 4, 5] as const;

/** Os eixos de Robertson (log × log, a moldura do motor). */
const EIXO_FR = { escala: 'log' as const, dominio: [xMinimo, xMaximo] as [number, number] };
const EIXO_Q = { escala: 'log' as const, dominio: [base, topo] as [number, number] };
const EIXO_U2 = { escala: 'linear' as const, dominio: [U2_MINIMO, U2_MAXIMO] as [number, number] };

/** As linhas de Robertson (SBTn e SBT: a mesma tabela). */
function linhasDeRobertson(): DesenhoDoAbaco['linhas'] {
  const F = FRONTEIRAS_ROBERTSON;
  return (Object.keys(F) as (keyof typeof F)[]).map((chave) => ({
    chave,
    pontos: pontosDaPolilinha(F[chave], chave === 'f23' || chave === 'divisa89'),
  }));
}

/** Congela o desenho inteiro: quem o recebe só lê (ele é calculado uma vez por tipo). */
function congelar(d: DesenhoDoAbaco): DesenhoDoAbaco {
  for (const l of d.linhas) {
    l.pontos.forEach((p) => Object.freeze(p));
    Object.freeze(l.pontos);
    Object.freeze(l);
  }
  d.rotulos.forEach((r) => Object.freeze(r));
  Object.freeze(d.linhas);
  Object.freeze(d.rotulos);
  Object.freeze(d.eixoX);
  Object.freeze(d.eixoY);
  return Object.freeze(d);
}

function calcularDesenho(tipo: TipoDeAbaco): DesenhoDoAbaco {
  if (tipo === 'sbtn' || tipo === 'sbt') {
    const sbtn = tipo === 'sbtn';
    return {
      tipo,
      titulo: sbtn ? 'SBTn · Qtn × Fr (Robertson 1990)' : 'SBT · qt/pa × Rf (Robertson 2010)',
      eixoX: { rotulo: sbtn ? 'Fr (%)' : 'Rf (%)', ...EIXO_FR },
      eixoY: { rotulo: sbtn ? 'Qtn' : 'qt/pa', ...EIXO_Q },
      linhas: linhasDeRobertson(),
      rotulos: rotulosDasZonas(sbtn ? zonaSbtn : zonaSbt, EIXO_FR, EIXO_Q, NOVE, String),
      fonte: null,
      semFronteiras: false,
    };
  }
  if (tipo === 'sbtnMod') {
    const siglas = ZONAS_SBTN_MOD as Readonly<Record<number, { sigla: string }>>;
    return {
      tipo,
      titulo: 'SBTn modificado · Qtn × Fr (Robertson 2016)',
      eixoX: { rotulo: 'Fr (%)', ...EIXO_FR },
      eixoY: { rotulo: 'Qtn', ...EIXO_Q },
      linhas: linhasDoSbtnMod(),
      rotulos: rotulosDasZonas((fr, qtn) => zonaSbtnMod(fr, qtn, ibDe(fr, qtn)), EIXO_FR, EIXO_Q, SETE,
        (z) => `${z} ${siglas[z].sigla}`),
      fonte: null,
      semFronteiras: false,
    };
  }
  if (tipo === 'schneider') {
    return {
      tipo,
      titulo: 'Schneider · Q × U₂ (Schneider et al. 2008)',
      eixoX: { rotulo: 'U₂ = Δu₂/σ\'v', ...EIXO_U2 },
      eixoY: { rotulo: 'Q = Qt1', ...EIXO_Q },
      linhas: linhasDoSchneider(),
      rotulos: rotulosDasZonas((u2, q) => zonaSchneider(q, u2), EIXO_U2, EIXO_Q, CINCO, (z) => ZONAS_SCHNEIDER[z].codigo),
      fonte: FONTE_SCHNEIDER,
      semFronteiras: false,
    };
  }
  return {
    tipo,
    titulo: 'Qt1 × Bq',
    eixoX: { rotulo: 'Bq', escala: 'linear', dominio: 'dados' },
    eixoY: { rotulo: 'Qt1', ...EIXO_Q },
    linhas: [],
    rotulos: [],
    fonte: FRASE_SEM_FRONTEIRAS_QT_BQ,
    semFronteiras: true,
  };
}

const desenhos = new Map<TipoDeAbaco, DesenhoDoAbaco>();

/** O desenho de um ábaco (calculado uma vez por tipo e congelado). */
export function desenhoDoAbaco(tipo: TipoDeAbaco): DesenhoDoAbaco {
  let d = desenhos.get(tipo);
  if (!d) {
    d = congelar(calcularDesenho(tipo));
    desenhos.set(tipo, d);
  }
  return d;
}

/** O x, o y e a zona (a cor) de cada leitura num ábaco, sem a borda. */
function brutosDoAbaco(tipo: TipoDeAbaco, r: Resultado): { x: ArrayLike<number>; y: ArrayLike<number>; zona: Int8Array } {
  const c = r.col;
  if (tipo === 'sbt') {
    const y = Float64Array.from(c.qt, (qt) => (1000 * qt) / PA_KPA);
    return { x: c.rf, y, zona: r.sbt };
  }
  if (tipo === 'sbtnMod') return { x: c.fr, y: c.qtn, zona: r.sbtnMod };
  if (tipo === 'schneider') return { x: c.U2, y: c.qt1, zona: r.sbtn };
  if (tipo === 'qtBq') return { x: c.bq, y: c.qt1, zona: r.sbtn };
  return { x: c.fr, y: c.qtn, zona: r.sbtn };
}

/** O lado da moldura de que um valor passa: −1 aquém, 1 além, 0 dentro (no log, o ≤ 0 está aquém). */
function lado(v: number, e: { escala: EscalaDoEixo; dominio: [number, number] | 'dados' }): number {
  if (e.dominio === 'dados') return 0;
  const [d0, d1] = e.dominio;
  if (v < d0 || (e.escala === 'log' && !(v > 0))) return -1;
  return v > d1 ? 1 : 0;
}

/** O valor levado à borda da moldura. */
const naBorda = (v: number, e: { dominio: [number, number] | 'dados' }, l: number): number =>
  e.dominio === 'dados' || l === 0 ? v : l < 0 ? e.dominio[0] : e.dominio[1];

/**
 * Os pontos de uma vertical num ábaco, um por linha do resultado (o índice de r): x e y nas unidades dos
 * eixos, o ponto de fora da moldura LEVADO À BORDA (NaN quando a conta não existe) e a zona da cor —
 *  sbtn: Fr × Qtn, `r.sbtn` · sbt: Rf × 1000·qt/pa, `r.sbt` · sbtnMod: Fr × Qtn, `r.sbtnMod` · schneider:
 *  U₂ × Qt1, `r.sbtn` (a cor) · qtBq: Bq × Qt1, `r.sbtn` (a cor). `naBorda` conta as leituras levadas à borda.
 */
export function pontosDoAbaco(
  tipo: TipoDeAbaco,
  r: Resultado,
): { x: Float64Array; y: Float64Array; zona: Int8Array; naBorda: number } {
  const d = desenhoDoAbaco(tipo);
  const b = brutosDoAbaco(tipo, r);
  const n = r.n;
  const x = new Float64Array(n);
  const y = new Float64Array(n);
  let fora = 0;
  for (let i = 0; i < n; i++) {
    const vx = b.x[i];
    const vy = b.y[i];
    if (!Number.isFinite(vx) || !Number.isFinite(vy)) {
      x[i] = NaN;
      y[i] = NaN;
      continue;
    }
    const lx = lado(vx, d.eixoX);
    const ly = lado(vy, d.eixoY);
    if (lx !== 0 || ly !== 0) fora++;
    x[i] = naBorda(vx, d.eixoX, lx);
    y[i] = naBorda(vy, d.eixoY, ly);
  }
  return { x, y, zona: Int8Array.from(b.zona.subarray(0, n)), naBorda: fora };
}

/** O símbolo de um eixo: o rótulo sem a unidade nem a definição («Fr (%)» → «Fr»; «Q = Qt1» → «Q»). */
const simbolo = (rotulo: string): string => rotulo.split(' ')[0];

/** «1 leitura» ou «1.788 leituras»: o plural certo e o ponto dos milhares. */
export const leituras = (n: number): string => (n === 1 ? '1 leitura' : `${numeroBr(n, 0)} leituras`);

/**
 * As colunas de que cada ábaco depende: a zona do próprio ábaco; no Schneider, a zona dele, os eixos (U₂ e
 * Qt1) e a cor dos pontos (a zona SBTn); no Qt1 × Bq, os eixos e a cor. O rótulo diz de onde vem cada porquê.
 */
const COLUNAS_DO_ABACO: Readonly<Record<TipoDeAbaco, readonly { coluna: ColunaDoMotor; rotulo?: string }[]>> = {
  sbtn: [{ coluna: 'sbtn' }],
  sbt: [{ coluna: 'sbt' }],
  sbtnMod: [{ coluna: 'sbtnMod' }],
  schneider: [{ coluna: 'schneider' }, { coluna: 'U2', rotulo: 'U₂' }, { coluna: 'qt1', rotulo: 'Qt1' },
    { coluna: 'sbtn', rotulo: 'a cor dos pontos (SBTn)' }],
  qtBq: [{ coluna: 'bq', rotulo: 'Bq' }, { coluna: 'qt1', rotulo: 'Qt1' }, { coluna: 'sbtn', rotulo: 'a cor dos pontos (SBTn)' }],
};

/**
 * A marca de homologação de um ábaco (R7) com as premissas da interpretação: a das colunas de que ele
 * depende, juntas (`combinarMarcas`) — no Schneider e no Qt1 × Bq a cor é a zona SBTn, e a marca dela entra.
 */
export function marcaDoAbaco(tipo: TipoDeAbaco, premissas: Premissas): MarcaDeHomologacao {
  return combinarMarcas(COLUNAS_DO_ABACO[tipo].map((c) => ({ marca: marcaDaColuna(c.coluna, premissas), rotulo: c.rotulo })));
}

/**
 * A frase das leituras levadas à borda, por lado: «24 leituras com U₂ < −2 e 3 com U₂ > 10 na borda»;
 * null quando nenhuma passa da moldura.
 */
export function fraseDaBorda(tipo: TipoDeAbaco, r: Resultado): string | null {
  const d = desenhoDoAbaco(tipo);
  const b = brutosDoAbaco(tipo, r);
  const lados: { texto: string; n: number }[] = [];
  for (const [eixo, valores] of [[d.eixoX, b.x], [d.eixoY, b.y]] as const) {
    if (eixo.dominio === 'dados') continue;
    let aquem = 0;
    let alem = 0;
    for (let i = 0; i < r.n; i++) {
      if (!Number.isFinite(b.x[i]) || !Number.isFinite(b.y[i])) continue;
      const l = lado(valores[i], eixo);
      if (l < 0) aquem++;
      else if (l > 0) alem++;
    }
    const s = simbolo(eixo.rotulo);
    const casas = (v: number): number => (Number.isInteger(v) ? 0 : 1);
    if (aquem > 0) lados.push({ texto: `${s} < ${numeroBr(eixo.dominio[0], casas(eixo.dominio[0]))}`, n: aquem });
    if (alem > 0) lados.push({ texto: `${s} > ${numeroBr(eixo.dominio[1], casas(eixo.dominio[1]))}`, n: alem });
  }
  if (lados.length === 0) return null;
  const partes = lados.map((l, k) => (k === 0 ? `${leituras(l.n)} com ${l.texto}` : `${numeroBr(l.n, 0)} com ${l.texto}`));
  const juntas = partes.length === 1 ? partes[0] : `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`;
  return `${juntas} na borda`;
}

/**
 * Os índices (de r) dos pontos dentro de um retângulo do ábaco, nas unidades dos eixos e com as pontas em
 * qualquer ordem — a seleção no ábaco que acende as leituras nas trilhas.
 */
export function indicesNoRetangulo(
  p: { x: Float64Array; y: Float64Array },
  ret: { x0: number; x1: number; y0: number; y1: number },
): number[] {
  const x0 = Math.min(ret.x0, ret.x1);
  const x1 = Math.max(ret.x0, ret.x1);
  const y0 = Math.min(ret.y0, ret.y1);
  const y1 = Math.max(ret.y0, ret.y1);
  const saida: number[] = [];
  const n = Math.min(p.x.length, p.y.length);
  for (let i = 0; i < n; i++) {
    const x = p.x[i];
    const y = p.y[i];
    if (x >= x0 && x <= x1 && y >= y0 && y <= y1) saida.push(i);
  }
  return saida;
}
